class EstimateShipping extends HTMLElement {
    static shippingRatesCache = new Map();
    static cepStateCache = new Map();

    constructor() {
        super();
        this.maxRetries = 1;
        this.retryDelay = 200;
        this.initialDelay = 100;
        this.cacheExpirationTime = 1000 * 60 * 30; // 30 minutos

        this.bindMethods();
        this.initElements();
    }

    bindMethods() {
        this.initShippingCalc = this.initShippingCalc.bind(this);
        this.generateShippingRates = this.generateShippingRates.bind(this);
        this.formatCEP = this.formatCEP.bind(this);
        this.getStateFromCEP = this.getStateFromCEP.bind(this);
    }

    initElements() {
        this.zipInput = this.querySelector('#es-shipping-calc-zip');
        this.provinceInput = this.querySelector('#es-shipping-calc-province');
        this.alertDanger = this.querySelector('#es-shipping-calc-alert-danger');
        this.alertWarning = this.querySelector('#es-shipping-calc-alert-warning');
        this.alertSuccess = this.querySelector('#es-shipping-calc-alert-success');
        this.shippingRatesDeliveryPeriodDays = this.getAttribute('data-shipping-rates-delivery-period-days');
        this.shippingRatesDeliveryPeriodDate = this.getAttribute('data-shipping-rates-delivery-period-date');
        this.showDeliveryDate = this.getAttribute('data-show-delivery-date') == 'true' ? true : false;
        this.showDeliveryDateFormat = this.getAttribute('data-show-delivery-date-format');

    }

    connectedCallback() {
        this.initShippingCalc();
        this.addEventListeners();
    }

    async initShippingCalc() {
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const cartFooter = document.querySelector('.cart__footer');

        if (cartDrawerItems && !cartDrawerItems.classList.contains('is-empty')) {
            document.querySelector('#CartDrawer #es-shipping-calc').style.display = 'block';
        }

        if (cartFooter) {
            document.querySelector('#MainContent #es-shipping-calc').style.display = 'block';
        }
    }

    addEventListeners() {
        if (this.zipInput) {
            this.zipInput.addEventListener('input', (event) => {
                event.target.value = this.formatCEP(event.target.value);

                // Limpa os resultados se o input estiver vazio
                if (!event.target.value.trim()) {
                    this.clearAlerts();
                    this.provinceInput.value = '';
                }
            });

            this.zipInput.addEventListener('blur', async (event) => {
                const state = await this.getStateFromCEP(event.target.value);
                this.provinceInput.value = state;
            });
        }

        const calculateButton = this.querySelector('#es-shipping-calc-btn');
        if (calculateButton) {
            calculateButton.addEventListener('click', (event) => {
                this.generateShippingRates(event.target);
            });
        }
    }

    formatCEP(cep) {
        cep = cep.replace(/\D/g, '');
        return cep.length > 5 ? `${cep.substring(0, 5)}-${cep.substring(5)}` : cep;
    }

    formatDeliveryPeriod(rate, format) {
        if (format === 'days') {
            const _days = rate.delivery_days[0] === rate.delivery_days[1] ? ` em até ${rate.delivery_days[0]}` : `entre ${rate.delivery_days[0]} a ${rate.delivery_days[1]}`;
            return this.shippingRatesDeliveryPeriodDays.replace('[days]', _days);
        } else if (format === 'date') {
            return this.shippingRatesDeliveryPeriodDate.replace('[day]', new Date(rate.delivery_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }));
        } else {
            return '';
        }
    }

    async getStateFromCEP(cep) {
        try {
            cep = cep.replace(/\D/g, '');
            if (cep.length !== 8) return '';

            // Verifica se existe no cache
            const cachedState = EstimateShipping.cepStateCache.get(cep);
            if (cachedState) {
                const { state, timestamp } = cachedState;
                // Verifica se o cache ainda é válido (não expirou)
                if (Date.now() - timestamp < this.cacheExpirationTime) {
                    return state;
                }
                // Se expirou, remove do cache
                EstimateShipping.cepStateCache.delete(cep);
            }

            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (!data.erro) {
                // Salva no cache
                EstimateShipping.cepStateCache.set(cep, {
                    state: data.uf,
                    timestamp: Date.now()
                });
                return data.uf || '';
            }
            return '';
        } catch (error) {
            return '';
        }
    }

    clearAlerts() {
        [this.alertDanger, this.alertWarning, this.alertSuccess].forEach(alert => {
            alert.innerHTML = '';
            alert.setAttribute('hidden', 'hidden');
        });
    }

    showAlert(type, message, isList = false) {
        const alert = this[`alert${type}`];
        alert.innerHTML = isList ? `<ul class="">${message}</ul>` : `<p class="">${message}</p>`;
        alert.removeAttribute('hidden');
    }

    async delay(ms) {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    toggleLoadingState(btn, isLoading) {
        const loadingSpinner = btn.querySelector('.loading__spinner');
        if (isLoading) {
            btn.classList.add('loading');
            loadingSpinner.classList.remove('hidden');
        } else {
            btn.classList.remove('loading');
            loadingSpinner.classList.add('hidden');
        }
    }

    async calculateShippingRates(zip, state) {
        // Verifica se existe no cache
        const cachedRates = EstimateShipping.shippingRatesCache.get(zip);
        if (cachedRates) {
            const { rates, timestamp } = cachedRates;
            // Verifica se o cache ainda é válido (não expirou)
            if (Date.now() - timestamp < this.cacheExpirationTime) {
                return { success: rates };
            }
            // Se expirou, remove do cache
            EstimateShipping.shippingRatesCache.delete(zip);
        }

        const params = `shipping_address[zip]=${zip}&shipping_address[country]=Brazil&shipping_address[province]=${state}`;

        const prepareResponse = await fetch(`/cart/prepare_shipping_rates.json?${params}`, {
            method: 'POST'
        });

        if (!prepareResponse.ok) {
            const data = await prepareResponse.json();
            return { error: data };
        }

        await this.delay(this.initialDelay);

        const asyncResponse = await fetch(`/cart/async_shipping_rates.json?${params}`);
        const data = await asyncResponse.json();

        // Salva no cache se a resposta for bem-sucedida
        if (data && !data.error) {
            EstimateShipping.shippingRatesCache.set(zip, {
                rates: data,
                timestamp: Date.now()
            });
        }

        return { success: data };
    }

    async generateShippingRates(btn, retryCount = 0) {
        this.toggleLoadingState(btn, true);
        this.clearAlerts();

        const zip = this.zipInput.value.replace(/\D/g, '');

        if (zip.length !== 8) {
            this.showAlert('Danger', 'Por favor, digite um CEP válido');
            this.toggleLoadingState(btn, false);
            return;
        }

        const state = await this.getStateFromCEP(zip);
        if (!state) {
            this.showAlert('Danger', 'CEP não encontrado');
            this.toggleLoadingState(btn, false);
            return;
        }

        this.provinceInput.value = state;

        try {
            if (retryCount === 0) await this.delay(this.initialDelay);

            const result = await this.calculateShippingRates(zip, state);

            if (result.error) {
                if (retryCount < this.maxRetries) {
                    await this.delay(this.retryDelay);
                    return this.generateShippingRates(btn, retryCount + 1);
                }

                const errorHtml = this.formatErrorMessage(result.error);
                this.showAlert('Danger', errorHtml, true);
                this.toggleLoadingState(btn, false);
                return;
            }

            const { shipping_rates = [] } = result.success;

            if (!shipping_rates.length) {
                if (retryCount < this.maxRetries) {
                    await this.delay(this.retryDelay);
                    return this.generateShippingRates(btn, retryCount + 1);
                }

                this.showAlert('Warning', 'Nenhuma opção de frete encontrada para este CEP.');
                this.toggleLoadingState(btn, false);
                return;
            }

            console.log(shipping_rates);
            const ratesHtml = shipping_rates.map(rate =>
                `<li>${rate.presentment_name}: <strong>R$ ${rate.price}</strong> ${this.showDeliveryDate ? `- ${this.formatDeliveryPeriod(rate, this.showDeliveryDateFormat)}` : ''}</li>`
            ).join('');

            this.showAlert('Success', ratesHtml, true);

        } catch (error) {
            if (retryCount < this.maxRetries) {
                await this.delay(this.retryDelay);
                return this.generateShippingRates(btn, retryCount + 1);
            }

            this.showAlert('Danger', 'Ocorreu um erro ao calcular o frete. Tente novamente.');
        }

        this.toggleLoadingState(btn, false);
    }

    formatErrorMessage(error) {
        if (!error || typeof error !== 'object') {
            return '<li>Erro ao processar o cálculo do frete</li>';
        }

        return Object.entries(error)
            .map(([key, value]) => `<li><strong>${key}</strong>: ${value.toString()}</li>`)
            .join('');
    }
}

customElements.define('estimate-shipping', EstimateShipping);
